import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Stack, Button, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CoordinatePatient } from '~/types/coordinate';

type WorkspaceTab = 'providers' | 'insurance' | 'family' | 'clinical' | 'tasks' | 'activity';
type ReadinessLevel = 'ok' | 'warn' | 'block';

interface ReadinessItem {
  id: string;
  label: string;
  detail: string;
  level: ReadinessLevel;
  tab?: WorkspaceTab;
  tabLabel?: string;
  highlightTaskIds?: string[];
}

function computeReadiness(patient: CoordinatePatient): ReadinessItem[] {
  const items: ReadinessItem[] = [];

  // Patient consent
  const consentOk = patient.patientContext?.consentDocumented === true;
  items.push({
    id: 'consent',
    label: 'Patient consent',
    detail: consentOk
      ? 'Consent documented'
      : 'Not documented — required before sharing PHI with the agency',
    level: consentOk ? 'ok' : 'block',
    tab: 'family',
    tabLabel: 'Document consent',
  });

  // Patient explanation letter
  const explanationDraft = (patient.agentDrafts ?? []).find(d => d.type === 'patient_explanation');
  if (explanationDraft) {
    const sent = explanationDraft.status === 'used';
    items.push({
      id: 'explanation',
      label: 'Patient explanation letter',
      detail: sent
        ? 'Sent to patient'
        : 'Drafted — review and send before the referral goes out',
      level: sent ? 'ok' : 'warn',
      tab: 'clinical',
      tabLabel: 'Review & send',
    });
  }

  // Active barriers
  const activeBarriers = (patient.barriers ?? []).filter(b => !b.resolved);
  const barrierLinkedTaskIds = activeBarriers
    .map(b => b.linkedTaskId)
    .filter((id): id is string => !!id);
  items.push({
    id: 'barriers',
    label: 'Documentation barriers',
    detail: activeBarriers.length === 0
      ? 'No active barriers'
      : `${activeBarriers.length} unresolved barrier${activeBarriers.length > 1 ? 's' : ''} — may delay start of care`,
    level: activeBarriers.length === 0 ? 'ok' : 'warn',
    tab: 'tasks',
    tabLabel: 'View tasks',
    highlightTaskIds: barrierLinkedTaskIds.length > 0 ? barrierLinkedTaskIds : undefined,
  });

  // Insurance
  if (patient.insuranceType === 'medicare') {
    const ins = patient.insuranceTrack;
    const confirmed = [
      ins?.homeboundDocumented,
      ins?.f2fEncounterComplete,
      ins?.physicianCertification485 === 'signed',
    ].filter(Boolean).length;
    items.push({
      id: 'insurance',
      label: 'Medicare documentation',
      detail: confirmed === 3
        ? 'All 3 requirements confirmed'
        : `${confirmed} of 3 requirements confirmed — agency may not bill until complete`,
      level: confirmed === 3 ? 'ok' : 'warn',
      tab: 'insurance',
      tabLabel: 'Complete documentation',
    });
  } else {
    const status = patient.insuranceTrack?.status ?? 'not_checked';
    const authOk = status === 'authorized' || status === 'not_required';
    const authPending = status === 'checking' || status === 'pending';
    items.push({
      id: 'insurance',
      label: 'Insurance authorization',
      detail: authOk
        ? status === 'not_required' ? 'No prior authorization required' : 'Authorization confirmed'
        : authPending
        ? 'Pending — referral can proceed, but confirm before start of care'
        : 'Not verified — check before the agency begins services',
      level: authOk ? 'ok' : 'warn',
      tab: 'insurance',
      tabLabel: authPending ? 'Check status' : 'Check authorization',
    });
  }

  return items;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSend: () => void;
  patient: CoordinatePatient;
  providerName: string;
  onNavigate: (tab: WorkspaceTab, highlightTaskIds?: string[]) => void;
}

const PreSendReviewDialog: React.FC<Props> = ({
  open, onClose, onSend, patient, providerName, onNavigate,
}) => {
  const items = computeReadiness(patient);
  const hasBlock = items.some(i => i.level === 'block');
  const hasWarn = items.some(i => i.level === 'warn');
  const allOk = !hasBlock && !hasWarn;

  const handleNavigate = (tab: WorkspaceTab, highlightTaskIds?: string[]) => {
    onClose();
    onNavigate(tab, highlightTaskIds);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>
          {allOk ? 'Ready' : 'Review before sending'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {providerName}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 1 }}>
        {allOk ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
            <CheckCircleIcon sx={{ color: '#00bda1', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              All requirements met. The referral is Ready.
            </Typography>
          </Stack>
        ) : (
          <Box>
            {items.map((item, i) => {
              const isOk = item.level === 'ok';
              const isBlock = item.level === 'block';
              const iconColor = isOk ? '#00bda1' : isBlock ? '#b71c1c' : '#e65100';

              return (
                <React.Fragment key={item.id}>
                  {i > 0 && <Divider />}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ py: 1.25, opacity: isOk ? 0.55 : 1 }}
                  >
                    {isOk
                      ? <CheckCircleIcon sx={{ fontSize: 17, color: iconColor, flexShrink: 0 }} />
                      : <WarningAmberIcon sx={{ fontSize: 17, color: iconColor, flexShrink: 0 }} />
                    }
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={isOk ? 400 : 600} sx={{ fontSize: '0.85rem' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color={isOk ? 'text.disabled' : 'text.secondary'} sx={{ fontSize: '0.75rem' }}>
                        {item.detail}
                      </Typography>
                    </Box>
                    {!isOk && item.tab && (
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '0.8rem !important' }} />}
                        onClick={() => handleNavigate(item.tab!, item.highlightTaskIds)}
                        sx={{
                          fontSize: '0.72rem', flexShrink: 0,
                          color: isBlock ? '#b71c1c' : '#153873',
                          borderColor: isBlock ? 'rgba(183,28,28,0.3)' : 'rgba(21,56,115,0.3)',
                        }}
                        variant="outlined"
                      >
                        {item.tabLabel}
                      </Button>
                    )}
                  </Stack>
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Stack alignItems="flex-end" spacing={0.5}>
          <Button
            variant="contained"
            startIcon={<SendIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={onSend}
            disabled={hasBlock}
            sx={{
              bgcolor: '#153873', '&:hover': { bgcolor: '#0b233f' },
              fontWeight: 700,
              '&.Mui-disabled': { bgcolor: 'rgba(21,56,115,0.3)' },
            }}
          >
            Send Referral
          </Button>
          {hasBlock && (
            <Typography variant="caption" color="error" sx={{ fontSize: '0.68rem' }}>
              Document patient consent to proceed
            </Typography>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PreSendReviewDialog;
